import { getLeadItemLabelParts, LEAD_ITEM_KIND_LABELS } from './leadsConfig';
/**
 * Agrupa consultas por lead (bandeja + “Consultas y leads” de un registro).
 * SQL 098: toda consulta nueva tiene lead; filas viejas sin lead quedan en orphans.
 */

function inquiryTime(row) {
  const t = Date.parse(row?.created_at || '');
  return Number.isFinite(t) ? t : 0;
}

function readId(value) {
  return String(value || '').trim();
}

/**
 * @param {object[]} [inquiries]
 * @param {object[]} [leads] De getrecordleads (opcional). Aporta name / temperature / via /
 *   interest_stage / offer_* / discard_reason (API DEV fdbc8aa).
 * @returns {{
 *   groups: Array<{
 *     leadId: string,
 *     leadName: string,
 *     temperature: string|null,
 *     via: string,
 *     interestStage: string|null,
 *     offerAmount: number|null,
 *     offerCurrency: string|null,
 *     discardReason: string|null,
 *     unseenInquiryCount: number,
 *     inquiries: object[],
 *     lastAt: number,
 *   }>,
 *   orphans: object[],
 * }}
 */
export function groupInquiriesByLead(inquiries = [], leads = []) {
  const leadList = Array.isArray(leads) ? leads.filter((lead) => readId(lead?.id)) : [];
  const leadById = new Map(leadList.map((lead) => [readId(lead.id), lead]));
  const groups = new Map();

  leadList.forEach((lead) => {
    const leadId = readId(lead.id);
    groups.set(leadId, {
      leadId,
      leadName: String(lead.name || '').trim() || 'Sin nombre',
      temperature: lead.temperature || null,
      via: String(lead.via || '').trim(),
      interestStage: lead.interest_stage || lead.interestStage || null,
      offerAmount: lead.offer_amount == null ? null : lead.offer_amount,
      offerCurrency: lead.offer_currency || null,
      discardReason: lead.discard_reason || null,
      unseenInquiryCount: Math.max(0, Number(lead.unseen_inquiry_count) || 0),
      inquiries: [],
      lastAt: 0,
    });
  });

  const orphans = [];
  (Array.isArray(inquiries) ? inquiries : []).forEach((inq) => {
    if (!inq) return;
    const leadId = readId(inq.lead_id);
    if (!leadId) {
      orphans.push(inq);
      return;
    }
    let group = groups.get(leadId);
    if (!group) {
      group = {
        leadId,
        leadName: String(inq.lead_name || '').trim() || 'Sin nombre',
        temperature: inq.lead_temperature ?? inq.temperature ?? null,
        via: '',
        interestStage: null,
        offerAmount: null,
        offerCurrency: null,
        discardReason: null,
        unseenInquiryCount: 0,
        inquiries: [],
        lastAt: 0,
      };
      groups.set(leadId, group);
    }
    const fromLead = leadById.get(leadId);
    if (fromLead) {
      group.leadName = String(fromLead.name || '').trim() || group.leadName;
      group.temperature = fromLead.temperature ?? null;
      group.via = String(fromLead.via || '').trim() || group.via;
      group.interestStage = fromLead.interest_stage || fromLead.interestStage || group.interestStage || null;
      group.offerAmount = fromLead.offer_amount == null ? null : fromLead.offer_amount;
      group.offerCurrency = fromLead.offer_currency || null;
      group.discardReason = fromLead.discard_reason || null;
      group.unseenInquiryCount = Math.max(0, Number(fromLead.unseen_inquiry_count) || 0);
    }
    if (!String(group.leadName || '').trim() || group.leadName === 'Sin nombre') {
      const fromInq = String(inq.lead_name || '').trim();
      if (fromInq) group.leadName = fromInq;
    }
    group.inquiries.push(inq);
    const at = inquiryTime(inq);
    if (at > group.lastAt) group.lastAt = at;
  });

  groups.forEach((group) => {
    group.inquiries = [...group.inquiries].sort((a, b) => inquiryTime(b) - inquiryTime(a));
    // Bandeja (sin leads[]): temperatura de la consulta más nueva (`lead_temperature`).
    if (!leadById.has(group.leadId) && group.inquiries[0]) {
      const top = group.inquiries[0];
      group.temperature = top.lead_temperature ?? top.temperature ?? null;
    }
    if (!group.unseenInquiryCount) {
      group.unseenInquiryCount = group.inquiries.reduce(
        (n, row) => n + (row?.seen_at ? 0 : 1),
        0,
      );
    }
  });

  const list = [...groups.values()].sort((a, b) => {
    const aHas = a.inquiries.length > 0;
    const bHas = b.inquiries.length > 0;
    if (aHas !== bHas) return aHas ? -1 : 1;
    if (b.lastAt !== a.lastAt) return b.lastAt - a.lastAt;
    return a.leadName.localeCompare(b.leadName, 'es');
  });

  orphans.sort((a, b) => inquiryTime(b) - inquiryTime(a));

  return { groups: list, orphans };
}


/**
 * Agrupa consultas de un lead por registro (lote / UF / parcela / lista).
 * Stage: de la consulta más reciente del registro (`interest_stage` /
 * offer_* / `discard_reason`, GET bandeja DEV `d81c785`); si null, no hay etapa.
 *
 * @param {object[]} [inquiries]
 * @returns {Array<{
 *   key: string,
 *   itemKind: string,
 *   kindLabel: string,
 *   title: string,
 *   interestStage: string|null,
 *   offerAmount: number|null,
 *   offerCurrency: string|null,
 *   discardReason: string|null,
 *   inquiries: object[],
 * }>}
 */
export function groupInquiriesByRecord(inquiries = []) {
  const list = Array.isArray(inquiries) ? inquiries.filter(Boolean) : [];
  const map = new Map();
  list.forEach((inq) => {
    const key = inquiryRecordKey(inq);
    if (!map.has(key)) {
      map.set(key, {
        key,
        itemKind: inferInquiryItemKind(inq),
        sample: inq,
        inquiries: [],
        lastAt: 0,
      });
    }
    const g = map.get(key);
    g.inquiries.push(inq);
    const at = inquiryTime(inq);
    if (at > g.lastAt) {
      g.lastAt = at;
      g.sample = inq;
    }
  });
  return [...map.values()]
    .sort((a, b) => b.lastAt - a.lastAt)
    .map((g) => {
      const sample = g.sample || g.inquiries[0] || {};
      const itemKind = g.itemKind;
      const parts = getLeadItemLabelParts(sample.item_label, itemKind);
      const title = parts.isCadastral
        ? (parts.secondary || parts.text || '—')
        : (parts.text || sample.item_label || getInquiryCollectionLabelFallback(sample) || '—');
      const stage = sample.interest_stage || sample.interestStage || null;
      return {
        key: g.key,
        itemKind,
        kindLabel: LEAD_ITEM_KIND_LABELS[itemKind] || 'Item',
        title,
        interestStage: stage,
        offerAmount: sample.offer_amount == null ? null : sample.offer_amount,
        offerCurrency: sample.offer_currency || null,
        discardReason: sample.discard_reason || null,
        inquiries: [...g.inquiries].sort((a, b) => inquiryTime(b) - inquiryTime(a)),
      };
    });
}

function inquiryRecordKey(inq) {
  const userLot = String(inq?.user_lot_id || '').trim();
  if (userLot) return `user_lot:${userLot}`;
  const parcel = String(inq?.parcel_id || '').trim();
  if (parcel) return `cad_parcel:${parcel}`;
  const lot = String(inq?.lot_id || '').trim();
  if (lot) return `cadastral_lot:${lot}`;
  const coll = String(inq?.collection_id || '').trim();
  const kind = String(inq?.collection_kind || '').trim() || 'list';
  return `collection:${kind}:${coll}`;
}

function inferInquiryItemKind(inq) {
  if (inq?.user_lot_id) return 'user_lot';
  if (inq?.parcel_id) return 'cad_parcel';
  if (inq?.lot_id) return 'cadastral_lot';
  if (inq?.collection_kind === 'cad') return 'showroom';
  return 'list';
}

function getInquiryCollectionLabelFallback(inq) {
  const name = String(inq?.collection_name || '').trim();
  return name || '';
}
